import axios from 'axios';

// Configuración base para el servicio de reservas
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const reservationAPI = axios.create({
  baseURL: `${API_BASE_URL}/reservation`,
  timeout: Number(process.env.REACT_APP_API_TIMEOUT) || 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para agregar token de autenticación cuando esté disponible
reservationAPI.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Control para evitar múltiples refresh simultáneos
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
};

// Interceptor para manejo de errores con refresh automático
reservationAPI.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return reservationAPI(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const token = localStorage.getItem('authToken');
      if (!token) {
        isRefreshing = false;
        localStorage.removeItem('user');
        window.location.reload();
        return Promise.reject(error);
      }

      try {
        const response = await axios.post(
          `${API_BASE_URL}/auth/refresh`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const newToken = response.data.access_token;
        localStorage.setItem('authToken', newToken);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        processQueue(null, newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return reservationAPI(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        window.location.reload();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Servicios de reservas
export const reservationService = {
  // Verificar disponibilidad (endpoint público)
  checkAvailability: async (checkIn, checkOut, adults, children) => {
    try {
      const params = new URLSearchParams({
        checkIn: checkIn.toISOString().split('T')[0], // Formato YYYY-MM-DD
        checkOut: checkOut.toISOString().split('T')[0],
        adults: adults.toString(),
        children: children.toString(),
      });

      const response = await reservationAPI.get(`/availability?${params}`);
      return response.data;
    } catch (error) {
      throw new Error('Error al verificar disponibilidad: ' + error.message);
    }
  },

  // Calcular precio esperado para un cuarto y rango (aplica overrides por día)
  calculatePrice: async ({ roomId, checkIn, checkOut, adults, children, userId }) => {
    try {
      const response = await reservationAPI.post('/calculate-price', {
        roomId,
        checkIn: checkIn instanceof Date ? checkIn.toISOString() : checkIn,
        checkOut: checkOut instanceof Date ? checkOut.toISOString() : checkOut,
        adults,
        children,
        ...(userId ? { userId } : {}),
      });
      return response.data;
    } catch (error) {
      throw new Error('Error al calcular precio: ' + error.message);
    }
  },

  // Crear una nueva reserva
  createReservation: async (reservationData) => {
    try {
      const response = await reservationAPI.post('/', reservationData);
      return response.data;
    } catch (error) {
      throw new Error('Error al crear la reserva: ' + error.message);
    }
  },

  // Obtener todas las reservas del usuario autenticado
  getMyReservations: async () => {
    try {
      const response = await reservationAPI.get('/my-reservations');
      return response.data;
    } catch (error) {
      throw new Error('Error al obtener mis reservas: ' + error.message);
    }
  },

  // Obtener todas las reservas (para admin)
  getAllReservations: async () => {
    try {
      const response = await reservationAPI.get('/');
      return response.data;
    } catch (error) {
      throw new Error('Error al obtener las reservas: ' + error.message);
    }
  },

  // Obtener una reserva por ID
  getReservationById: async (id) => {
    try {
      const response = await reservationAPI.get(`/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(`Error al obtener la reserva ${id}: ` + error.message);
    }
  },

  // Actualizar una reserva
  updateReservation: async (id, reservationData) => {
    try {
      const response = await reservationAPI.patch(`/${id}`, reservationData);
      return response.data;
    } catch (error) {
      throw new Error(`Error al actualizar la reserva ${id}: ` + error.message);
    }
  },

  // Cancelar una reserva
  cancelReservation: async (id) => {
    try {
      const response = await reservationAPI.patch(`/${id}`, {
        status: 'CANCELLED'
      });
      return response.data;
    } catch (error) {
      throw new Error(`Error al cancelar la reserva ${id}: ` + error.message);
    }
  },

  // Eliminar una reserva (para admin)
  deleteReservation: async (id) => {
    try {
      const response = await reservationAPI.delete(`/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(`Error al eliminar la reserva ${id}: ` + error.message);
    }
  },

  // Obtener todos los pagos de una reserva
  getReservationPayments: async (reservationId) => {
    try {
      const response = await reservationAPI.get(`/${reservationId}/payments`);
      return response.data;
    } catch (error) {
      throw new Error(`Error al obtener los pagos de la reserva ${reservationId}: ` + error.message);
    }
  },

  // Configurar los pagos de una reserva (FULL o HALF)
  setupPayments: async (reservationId, paymentOption) => {
    try {
      const response = await reservationAPI.post(`/${reservationId}/setup-payments`, {
        paymentOption: paymentOption,
      });
      return response.data;
    } catch (error) {
      throw new Error(`Error al configurar los pagos: ` + error.message);
    }
  },

  // Actualizar una transacción específica con el comprobante
  updateTransaction: async (reservationId, transactionId, transactionData) => {
    try {
      // Usar el endpoint de transactions directamente
      const response = await axios.patch(
        `${API_BASE_URL}/transaction/${transactionId}`,
        transactionData,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      throw new Error(`Error al actualizar la transacción: ` + error.message);
    }
  },

  // Obtener métodos de pago disponibles
  getPaymentMethods: async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/payment-method`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`,
        },
      });
      return response.data;
    } catch (error) {
      throw new Error(`Error al obtener métodos de pago: ` + error.message);
    }
  },

  // Obtener check-in de una reserva
  getCheckIn: async (reservationId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/check-in/reservation/${reservationId}`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`,
        },
      });
      return response.data;
    } catch (error) {
      // Si no existe check-in, retornar null en lugar de error
      if (error.response?.status === 404) {
        return null;
      }
      throw new Error(`Error al obtener check-in: ` + error.message);
    }
  },

  // Crear check-in para una reserva
  createCheckIn: async (checkInData) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/check-in`, checkInData, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`,
        },
      });
      return response.data;
    } catch (error) {
      throw new Error(`Error al crear check-in: ` + error.message);
    }
  },

  // Actualizar check-in
  updateCheckIn: async (checkInId, checkInData) => {
    try {
      const response = await axios.patch(`${API_BASE_URL}/check-in/${checkInId}`, checkInData, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`,
        },
      });
      return response.data;
    } catch (error) {
      throw new Error(`Error al actualizar check-in: ` + error.message);
    }
  },
};

export default reservationService;